/**
 * SearchForm - Formulario de búsqueda avanzada
 */

import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Search, X, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { useSucursal, ALL_SUCURSALES_ID } from "@/contexts/sucursal-context";
import { busquedaSchema, type BusquedaFormData } from "../utils/search-validation";
import type { BusquedaParams } from "@/hooks/use-busqueda-avanzada";

interface SearchFormProps {
  onSearch: (data: BusquedaFormData) => void;
  onClear: () => void;
  isLoading: boolean;
  initialValues?: Partial<BusquedaParams>;
  autoFocus?: boolean;
}

export function SearchForm({
  onSearch,
  onClear,
  isLoading,
  initialValues,
  autoFocus,
}: SearchFormProps) {
  const { t } = useTranslation();
  const { sucursales, canViewAllSucursales, isAllSucursalesSelected, sucursalActiva } =
    useSucursal();
  const [showFilters, setShowFilters] = useState(false);
  const identidadRef = useRef<HTMLInputElement>(null);

  // Determinar sucursal por defecto según contexto
  const defaultSucursal =
    initialValues?.sucursal ??
    (!canViewAllSucursales && sucursalActiva ? sucursalActiva.id : undefined) ??
    (canViewAllSucursales &&
    !isAllSucursalesSelected &&
    sucursalActiva &&
    sucursalActiva.id !== ALL_SUCURSALES_ID
      ? sucursalActiva.id
      : undefined);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<BusquedaFormData>({
    resolver: zodResolver(busquedaSchema),
    defaultValues: {
      identidad: initialValues?.identidad ?? "",
      nombres: initialValues?.nombres ?? "",
      apellidos: initialValues?.apellidos ?? "",
      numero_orden: initialValues?.numero_orden ?? "",
      examen: initialValues?.examen ?? "",
      sucursal: initialValues?.sucursal ?? defaultSucursal,
      fecha_desde: initialValues?.fecha_desde ?? "",
      fecha_hasta: initialValues?.fecha_hasta ?? "",
    },
  });

  // Auto-focus on identidad field when sheet opens
  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => identidadRef.current?.focus(), 100);
    }
  }, [autoFocus]);

  const handleClear = () => {
    reset({
      identidad: "",
      nombres: "",
      apellidos: "",
      numero_orden: "",
      examen: "",
      sucursal: defaultSucursal,
      fecha_desde: "",
      fecha_hasta: "",
    });
    onClear();
  };

  // Get root-level form error (at_least_one_field_required)
  const formError = (errors as Record<string, { message?: string }>)?.["_form"]?.message;

  return (
    <form onSubmit={handleSubmit(onSearch)} className="space-y-3">
      {/* Main search fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="identidad" className="text-xs">
            {t("busquedaAvanzada.fields.identidad")}
          </Label>
          <Input
            id="identidad"
            {...register("identidad")}
            ref={(e) => {
              register("identidad").ref(e);
              (identidadRef as React.MutableRefObject<HTMLInputElement | null>).current = e;
            }}
            placeholder={t("busquedaAvanzada.placeholders.identidad")}
            className="h-8 text-sm"
          />
          {errors.identidad && (
            <p className="text-destructive text-xs">{t("busquedaAvanzada.errors.minLength")}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="numero_orden" className="text-xs">
            {t("busquedaAvanzada.fields.numeroOrden")}
          </Label>
          <Input
            id="numero_orden"
            {...register("numero_orden")}
            placeholder={t("busquedaAvanzada.placeholders.numeroOrden")}
            className="h-8 text-sm"
          />
          {errors.numero_orden && (
            <p className="text-destructive text-xs">{t("busquedaAvanzada.errors.minLength")}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="nombres" className="text-xs">
            {t("busquedaAvanzada.fields.nombres")}
          </Label>
          <Input
            id="nombres"
            {...register("nombres")}
            placeholder={t("busquedaAvanzada.placeholders.nombres")}
            className="h-8 text-sm"
          />
          {errors.nombres && (
            <p className="text-destructive text-xs">{t("busquedaAvanzada.errors.minLength")}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="apellidos" className="text-xs">
            {t("busquedaAvanzada.fields.apellidos")}
          </Label>
          <Input
            id="apellidos"
            {...register("apellidos")}
            placeholder={t("busquedaAvanzada.placeholders.apellidos")}
            className="h-8 text-sm"
          />
          {errors.apellidos && (
            <p className="text-destructive text-xs">{t("busquedaAvanzada.errors.minLength")}</p>
          )}
        </div>

        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="examen" className="text-xs">
            {t("busquedaAvanzada.fields.examen")}
          </Label>
          <Input
            id="examen"
            {...register("examen")}
            placeholder={t("busquedaAvanzada.placeholders.examen")}
            className="h-8 text-sm"
          />
          {errors.examen && (
            <p className="text-destructive text-xs">{t("busquedaAvanzada.errors.minLength")}</p>
          )}
        </div>
      </div>

      {/* Collapsible filters */}
      <button
        type="button"
        onClick={() => setShowFilters(!showFilters)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {showFilters ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {t("busquedaAvanzada.filters.advanced")}
      </button>

      {showFilters && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
          <div className="space-y-1">
            <Label className="text-xs">{t("busquedaAvanzada.fields.sucursal")}</Label>
            {canViewAllSucursales ? (
              <Select
                defaultValue={defaultSucursal ? String(defaultSucursal) : "all"}
                onValueChange={(val) =>
                  setValue("sucursal", val === "all" ? undefined : Number(val))
                }
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder={t("common.all")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  {sucursales.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center h-8 px-3 border rounded-md bg-muted/50 text-sm">
                <span className="truncate">{sucursalActiva?.nombre ?? "-"}</span>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <Label className="text-xs">{t("busquedaAvanzada.fields.fechaDesde")}</Label>
            <DatePicker
              value={undefined}
              onChange={(val) => setValue("fecha_desde", val)}
              placeholder={t("busquedaAvanzada.placeholders.fechaDesde")}
              className="h-8 text-sm"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">{t("busquedaAvanzada.fields.fechaHasta")}</Label>
            <DatePicker
              value={undefined}
              onChange={(val) => setValue("fecha_hasta", val)}
              placeholder={t("busquedaAvanzada.placeholders.fechaHasta")}
              className="h-8 text-sm"
            />
          </div>
        </div>
      )}

      {/* Form-level error */}
      {formError && (
        <p className="text-destructive text-xs">{t("busquedaAvanzada.errors.paramsRequired")}</p>
      )}

      {/* Action buttons */}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={isLoading} className="flex-1">
          {isLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Search className="mr-2 h-4 w-4" />
          )}
          {t("busquedaAvanzada.buttons.buscar")}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={handleClear}>
          <X className="mr-2 h-4 w-4" />
          {t("busquedaAvanzada.buttons.limpiar")}
        </Button>
      </div>
    </form>
  );
}
